import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FiltersState, SelectedFilterNames, SelectedFilters } from '@model/state';
import {
  FilterMappingValue,
  FilterNames,
  FiltersSideBarType,
  MultiSelectFilterVariants,
  SortByOptions,
  SpecialFilters
} from '@model/filters';
import { getFlightAvailabilityFilters, getFlightAvailabilityFlights } from '@state/search-results/flights';
import { getFilteredTokens } from '@util/filters';
import { getSelectedFilters } from './filterSelectors';
import {
  getHasTourExtensions,
  getToursFilters,
  getToursSearchResults
} from '@state/slices/search-results/tours/toursSelectors';
import { HYDRATE } from 'next-redux-wrapper';
import { isEqual } from 'lodash';
import { GlobalActions, GlobalAppState } from '@state/store';
import { FlightFilters } from '@model/iceberg';
import { FilterTypesMapping } from '@config/filters';
import {
  FromPricePackagesActions,
  getFromPricePackageFilters,
  getFromPricePackages,
  fetchTours
} from '@state/search-results';
import { DealFinderResultsActions } from '@state/slices/deal-finder-results/dealFinderResultsSlice';

/* ***************** *
 *       TYPES       *
 * ***************** */

export interface FiltersAction {
  type: FiltersActionTypes | GlobalActions;
  payload?: any;
}

/* ***************** *
 *   ACTION TYPES    *
 * ***************** */

export enum FiltersActionTypes {
  NAME = '@FILTERS',
  SET_FILTERED_TOKENS = '@FILTERS/setFilteredTokens',
  SET_SELECTED_FILTERS = '@FILTERS/setSelectedFilters',
  RESET_SELECTED_FILTERS = '@FILTERS/resetSelectedFilters',
  SET_SORTING_OPTION = '@FILTERS/setSortingOption',
  RESET_FLIGHT_FILTERS = '@FILTERS/resetFlightFilters',
  RESET_FLIGHT_FILTER_TOKEN = '@FILTERS/RESET_FLIGHT_FILTER_TOKEN',
  FILTER_TOKENS = '@FILTERS/FILTER_TOKENS'
}

/* ***************** *
 *     ACTIONS       *
 * ***************** */

export interface FilteredTokensPayload {
  type: FiltersSideBarType;
  filteredTokens: Array<string>;
}

export interface SetFilteredTokens {
  type: FiltersActionTypes.SET_FILTERED_TOKENS;
  payload: FilteredTokensPayload;
}

export interface SelectedFiltersPayload {
  type: FiltersSideBarType;
  selectedFilters: SelectedFilterNames;
}

export interface SetSelectedFilters {
  type: FiltersActionTypes.SET_SELECTED_FILTERS;
  payload: SelectedFiltersPayload;
}

export interface SetSortingOption {
  type: FiltersActionTypes.SET_SORTING_OPTION;
  payload: SortByOptions;
}

export interface ResetSelectedFilters {
  type: FiltersActionTypes.RESET_SELECTED_FILTERS;
  payload: FiltersSideBarType;
}

export const resetFlightFilterTokens = createAsyncThunk(
  FiltersActionTypes.RESET_FLIGHT_FILTER_TOKEN,
  async (_, { getState, dispatch }) => {
    const state = getState() as GlobalAppState;

    const filters: FlightFilters | undefined = getFlightAvailabilityFilters(state);
    if (!filters) return;
    const selectedFilters: SelectedFilterNames = getInitialFlightFilters(filters);

    dispatch(
      filtersSlice.actions.setSelectedFilters({
        type: FiltersSideBarType.FLIGHTS,
        selectedFilters
      })
    );
  }
);

export function getInitialFlightFilters(filters: FlightFilters): SelectedFilterNames {
  return Object.entries(filters).reduce(
    (acc: SelectedFilterNames, [key, value]: any) => {
      if (Object.keys(value).length <= 1) return acc;
      const filterName: FilterMappingValue = FilterTypesMapping[key];
      let filters: Array<string> = [];
      if (filterName.variant === MultiSelectFilterVariants.SELECT) {
        filters = [SpecialFilters.ALL];
      }
      if (filterName.variant === MultiSelectFilterVariants.UNSELECT) {
        filters = Object.keys(value);
      }
      return {
        ...acc,
        [key]: filters
      };
    },
    {
      [FilterNames.OUTBOUND_DEPARTURE_TIME]: [SpecialFilters.ALL],
      [FilterNames.RETURN_DEPARTURE_TIME]: [SpecialFilters.ALL]
    }
  );
}

export const filterTokens = createAsyncThunk(
  FiltersActionTypes.FILTER_TOKENS,
  async (action: any, { getState, dispatch }) => {
    const state = getState() as GlobalAppState;
    const searchType: FiltersSideBarType = action.payload.type;

    let filtersSelector: any;
    let noOfFixedFilters: number = 0; // number of extra FE-only filters

    switch (searchType) {
      case FiltersSideBarType.SEARCH:
        filtersSelector = getFromPricePackageFilters;
        break;
      case FiltersSideBarType.FLIGHTS:
        filtersSelector = getFlightAvailabilityFilters;
        noOfFixedFilters = 2;
        break;
      case FiltersSideBarType.TOURS: {
        filtersSelector = getToursFilters;
        const hasExtensions = getHasTourExtensions(state);
        noOfFixedFilters = hasExtensions ? 1 : 0;
      }
    }

    const filters = filtersSelector(state);

    const activeFilters = Object.entries(filters).reduce((acc: any, [filterName, filterValue]: any) => {
      if (Object.keys(filterValue).length > 1 || typeof filterValue !== 'object') {
        return { ...acc, [filterName]: filterValue };
      }
      return acc;
    }, {});

    const selectedFilters: SelectedFilters = getSelectedFilters(state, searchType) as unknown as SelectedFilters;

    const shouldFilterTokens =
      Object.keys(activeFilters).length + noOfFixedFilters === Object.keys(selectedFilters).length;

    if (shouldFilterTokens) {
      const availablePackagesData = getFromPricePackages(state);
      const availableFlightsData = getFlightAvailabilityFlights(state);
      const availableToursData = getToursSearchResults(state);

      const getSearchData = () => {
        switch (searchType) {
          case FiltersSideBarType.FLIGHTS:
            return availableFlightsData;
          case FiltersSideBarType.TOURS:
            return availableToursData;
          default:
            return availablePackagesData || [];
        }
      };

      const filteredTokenList = getFilteredTokens(searchType, getSearchData(), selectedFilters);

      dispatch(
        setFilteredTokens({
          type: searchType,
          filteredTokens: filteredTokenList || []
        })
      );
    }
  }
);

/* ***************** *
 *     REDUCER       *
 * ***************** */

export const FILTERS_INITIAL_STATE: FiltersState = {
  filteredTokens: {},
  selectedFilters: {},
  sorting: SortByOptions.POPULAR
};

const filtersSlice = createSlice({
  name: FiltersActionTypes.NAME,
  initialState: FILTERS_INITIAL_STATE,
  reducers: {
    setFilteredTokens: (state, action: PayloadAction<FilteredTokensPayload>) => {
      const { type, filteredTokens } = action.payload;
      state.filteredTokens[type] = filteredTokens;
    },
    setSelectedFilters: (state, action: PayloadAction<SelectedFiltersPayload>) => {
      const { type, selectedFilters } = action.payload;
      state.selectedFilters[type] = {
        ...state.selectedFilters[type],
        ...selectedFilters
      };
    },
    resetSelectedFilters: (state, action: PayloadAction<FiltersSideBarType>) => {
      // @ts-ignore
      state.selectedFilters[action.payload] = [];
    },
    setSortingOption: (state, action: PayloadAction<SortByOptions>) => {
      state.sorting = action.payload;
    },
    resetFlightFilters: (state) => {
      state.selectedFilters[FiltersSideBarType.FLIGHTS] = {
        outboundDepartureTime: [SpecialFilters.ALL],
        returnDepartureTime: [SpecialFilters.ALL]
      };
    },
    clearFilters: () => FILTERS_INITIAL_STATE
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        if (isEqual(state, FILTERS_INITIAL_STATE)) {
          return action.payload?.filters || state;
        }
        return state;
      })
      .addCase(FromPricePackagesActions.CLEAR_FROM_PRICE_PACKAGES, () => FILTERS_INITIAL_STATE)
      .addCase(DealFinderResultsActions.CLEAR_RESULTS, () => FILTERS_INITIAL_STATE)
      .addCase(fetchTours.fulfilled, () => FILTERS_INITIAL_STATE);
  }
});

export const {
  setFilteredTokens,
  setSelectedFilters,
  resetSelectedFilters,
  setSortingOption,
  resetFlightFilters,
  clearFilters
} = filtersSlice.actions;

// Export reducer
export const filtersReducer = filtersSlice.reducer;
